import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import colors from './colors';

const ThemeContext = createContext();
ThemeContext.displayName = 'ThemeContext';

// Apply CSS variables to document root
function applyCssVariables(mode) {
  const c = colors[mode];
  const root = document.documentElement;

  root.setAttribute('data-theme', mode);

  const vars = {
    '--bg-primary': c.bgPrimary,
    '--bg-secondary': c.bgSecondary,
    '--bg-card': c.bgCard,
    '--bg-card-hover': c.bgCardHover,
    '--bg-sidebar': c.bgSidebar,
    '--bg-input': c.bgInput,
    '--bg-elevated': c.bgElevated,
    '--bg-overlay': c.bgOverlay,
    '--border-primary': c.borderPrimary,
    '--border-secondary': c.borderSecondary,
    '--border-accent': c.borderAccent,
    '--text-primary': c.textPrimary,
    '--text-secondary': c.textSecondary,
    '--text-muted': c.textMuted,
    '--text-inverse': c.textInverse,
    '--accent-primary': c.accentPrimary,
    '--accent-secondary': c.accentSecondary,
    '--accent-tertiary': c.accentTertiary,
    '--accent-glow': c.accentGlow,
    '--gradient': c.gradient,
    '--gradient-subtle': c.gradientSubtle,
    '--color-success': c.success,
    '--color-success-bg': c.successBg,
    '--color-warning': c.warning,
    '--color-warning-bg': c.warningBg,
    '--color-error': c.error,
    '--color-error-bg': c.errorBg,
    '--color-info': c.info,
    '--color-info-bg': c.infoBg,
  };

  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('theme-mode');
    return saved || 'dark';
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const [sidebarMobile, setSidebarMobile] = useState(false);

  useEffect(() => {
    applyCssVariables(mode);
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
        setSidebarMobile(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth < 1024) {
      setSidebarMobile((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setSidebarMobile(false);
  }, []);

  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === 'dark',
      toggleMode,
      colors: colors[mode],
      sidebarCollapsed,
      sidebarMobile,
      toggleSidebar,
      closeMobileSidebar,
      setSidebarCollapsed,
    }),
    [mode, toggleMode, sidebarCollapsed, sidebarMobile, toggleSidebar, closeMobileSidebar]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return context;
}

// Backward compatibility - maps to old MaterialUIController interface
export function useMaterialUIController() {
  const theme = useAppTheme();
  const controller = {
    miniSidenav: theme.sidebarCollapsed,
    transparentSidenav: false,
    whiteSidenav: !theme.isDark,
    sidenavColor: 'info',
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: 'ltr',
    layout: 'dashboard',
    darkMode: theme.isDark,
  };

  const dispatch = (action) => {
    if (action.type === 'MINI_SIDENAV') {
      theme.setSidebarCollapsed(action.value);
    }
    if (action.type === 'DARKMODE') {
      if (action.value !== theme.isDark) theme.toggleMode();
    }
  };

  return [controller, dispatch];
}

// Backward-compatible dispatch functions
export const setMiniSidenav = (dispatch, value) => dispatch({ type: 'MINI_SIDENAV', value });
export const setDarkMode = (dispatch, value) => dispatch({ type: 'DARKMODE', value });
export const setTransparentSidenav = () => {};
export const setWhiteSidenav = () => {};
export const setSidenavColor = () => {};
export const setTransparentNavbar = () => {};
export const setFixedNavbar = () => {};
export const setOpenConfigurator = () => {};
export const setDirection = () => {};
export const setLayout = () => {};

// Re-export provider with old name for compatibility
export const MaterialUIControllerProvider = AppThemeProvider;
